/**
 * 通用样式 - 布局部分
 *
 * 页面容器、卡片、Flex、面板、列表项、定位等布局相关样式。
 * 由 common.ts 组合使用，不直接对外导出。
 */

import type { StyleDeps } from './style-types';

/**
 * 布局样式定义
 */
export function layoutStyles({ token: t, css }: StyleDeps) {
  return {
    // ==================== 页面 / 容器 ====================

    /** 页面容器 */
    pageContainer: css`
      padding: ${t.paddingLG}px;
      min-height: 100%;
    `,

    /** 卡片包装器 */
    cardWrapper: css`
      background: ${t.colorBgContainer};
      border-radius: ${t.borderRadiusLG}px;
      padding: ${t.paddingLG}px;
      box-shadow: ${t.boxShadowTertiary};
    `,

    /** 表格包装器 */
    tableWrapper: css`
      .ant-pro-table-search {
        margin-bottom: ${t.marginMD}px;
        padding: ${t.paddingMD}px;
        background: ${t.colorBgContainer};
        border-radius: ${t.borderRadiusLG}px;
      }

      .ant-table-wrapper {
        background: ${t.colorBgContainer};
        border-radius: ${t.borderRadiusLG}px;
      }
    `,

    /** 表单包装器 */
    formWrapper: css`
      max-width: 800px;
      margin: 0 auto;
      padding: ${t.paddingLG}px;
    `,

    /** 详情页头部 */
    detailHeader: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: ${t.marginLG}px;
      padding-bottom: ${t.paddingMD}px;
      border-bottom: 1px solid ${t.colorBorderSecondary};
    `,

    /** 操作栏 */
    actionBar: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: ${t.marginMD}px;
      flex-wrap: wrap;
      gap: ${t.marginSM}px;
    `,

    /** 搜索/筛选栏 */
    filterBar: css`
      display: flex;
      align-items: center;
      gap: ${t.marginSM}px;
      margin-bottom: ${t.marginMD}px;
      flex-wrap: wrap;
    `,

    /** 统计卡片 */
    statCard: css`
      background: ${t.colorBgContainer};
      border-radius: ${t.borderRadiusLG}px;
      padding: ${t.paddingLG}px;
      text-align: center;

      .stat-value {
        font-size: ${t.fontSizeHeading2}px;
        font-weight: ${t.fontWeightStrong};
        color: ${t.colorTextHeading};
        line-height: 1.2;
      }

      .stat-label {
        font-size: ${t.fontSize}px;
        color: ${t.colorTextSecondary};
        margin-top: ${t.marginXS}px;
      }
    `,

    /** 空状态容器 */
    emptyState: css`
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: ${Number(t.paddingLG) * 2}px;
      color: ${t.colorTextSecondary};
    `,

    /** 加载状态容器 */
    loadingContainer: css`
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
    `,

    // ==================== 容器样式 ====================

    /** 灰色背景容器 */
    grayContainer: css`
      background: ${t.colorBgLayout};
      padding: ${t.paddingSM}px;
      border-radius: ${t.borderRadiusSM}px;
    `,

    /** 可滚动灰色容器 */
    scrollableGrayContainer: css`
      max-height: 300px;
      overflow: auto;
      background: ${t.colorBgLayout};
      padding: ${t.paddingSM}px;
      border-radius: ${t.borderRadiusSM}px;
    `,

    // ==================== 头像样式 ====================

    /** 小头像 */
    avatarSmall: css`
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    `,

    /** 头像占位符 */
    avatarPlaceholder: css`
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: ${t.colorBgLayout};
      display: flex;
      align-items: center;
      justify-content: center;
    `,

    /** 大头像 */
    avatarLarge: css`
      width: 60px;
      height: 60px;
      border-radius: 50%;
      object-fit: cover;
    `,

    /** 大头像占位符 */
    avatarLargePlaceholder: css`
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: ${t.colorBgLayout};
    `,

    // ==================== Flex 布局 ====================

    /** 两列布局行 */
    twoColumnRow: css`
      display: flex;
      gap: ${t.marginLG}px;
    `,

    /** 列项 */
    columnItem: css`
      flex: 1;
    `,

    /** 水平居中对齐 */
    flexCenter: css`
      display: flex;
      align-items: center;
      gap: ${t.marginXS}px;
    `,

    /** 垂直居中对齐 */
    flexCenterColumn: css`
      display: flex;
      flex-direction: column;
      align-items: center;
    `,

    /** 水平垂直居中 */
    flexCenterBoth: css`
      display: flex;
      justify-content: center;
      align-items: center;
    `,

    /** 两端对齐 */
    flexBetween: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
    `,

    /** 全高度 Flex 容器 - 用于填充剩余空间 */
    fullHeightFlex: css`
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0;
    `,

    /** 全屏高度 Flex 容器 */
    fullViewFlex: css`
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    `,

    // ==================== 页面布局 ====================

    /** 吸顶头部 */
    stickyHeader: css`
      position: sticky;
      top: 0;
      z-index: 100;
      background: ${t.colorBgContainer};
      flex-shrink: 0;
    `,

    /** 页面头部栏 */
    pageHeaderBar: css`
      padding: 0 ${t.paddingLG}px;
      border-bottom: 1px solid ${t.colorBorderSecondary};
      display: flex;
      align-items: center;
      min-height: 48px;
      position: relative;
    `,

    /** 内容区域居中布局 */
    contentCentered: css`
      padding: ${t.paddingLG}px;
      display: flex;
      justify-content: center;
    `,

    /** 内容卡片容器 */
    contentCard: css`
      max-width: 1000px;
      width: 100%;
    `,

    /** 详情内容区域 */
    detailContentSection: css`
      margin-top: ${t.marginMD}px;
    `,

    /** 详情内容框 */
    detailContentBox: css`
      margin-top: ${t.marginXS}px;
      padding: ${t.paddingSM}px;
      background: ${t.colorBgLayout};
      border-radius: ${t.borderRadiusSM}px;
      white-space: pre-wrap;
    `,

    // ==================== 列表项 ====================

    /** 列表项基础 */
    listItem: css`
      padding: ${t.paddingXS}px;
      cursor: pointer;
      margin-bottom: ${t.marginXXS}px;
      border-radius: ${t.borderRadiusSM}px;
      transition: all 0.2s;
    `,

    /** 列表项选中态 */
    listItemActive: css`
      background: ${t.colorPrimaryBg};
      border-left: 3px solid ${t.colorPrimary};
    `,

    /** 列表项非选中态 */
    listItemInactive: css`
      background: transparent;
      border-left: 3px solid transparent;
    `,

    // ==================== 面板 ====================

    /** 侧边面板 */
    sidePanel: css`
      width: 400px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      background: ${t.colorBgLayout};
      overflow: hidden;
      border-right: 1px solid ${t.colorBorderSecondary};
    `,

    /** 面板头部 */
    panelHeader: css`
      padding: ${t.paddingSM}px ${t.paddingMD}px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid ${t.colorBorderSecondary};
      flex-shrink: 0;
      background: ${t.colorBgContainer};
    `,

    /** 面板内容滚动区 */
    panelContent: css`
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: ${t.paddingXS}px;
      min-height: 0;
    `,

    // ==================== 定位 ====================

    /** 相对定位 */
    positionRelative: css`
      position: relative;
    `,

    /** 绝对定位 */
    positionAbsolute: css`
      position: absolute;
    `,

    /** 固定定位 */
    positionFixed: css`
      position: fixed;
    `,

    // ==================== 全屏加载 ====================

    /** 全屏加载容器 */
    loadingContainerFullscreen: css`
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
    `,
  };
}
